import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MFACodeInput } from '@/components/MFACodeInput';
import { BackupCodesDisplay } from '@/components/BackupCodesDisplay';
import { useMFA } from '@/hooks/useMFA';
import { generateBackupCodes, hashBackupCode } from '@/utils/mfaHelpers';
import { supabase } from '@/integrations/supabase/client';
import { QRCodeSVG } from 'qrcode.react';
import { Shield, ArrowLeft, Copy, Check, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export default function MFASetup() {
  const navigate = useNavigate();
  const { enrollMFA, verifyMFA } = useMFA();
  const { toast } = useToast();
  
  const [step, setStep] = useState<'enroll' | 'verify' | 'backup'>('enroll');
  const [qrCode, setQrCode] = useState<string>('');
  const [secret, setSecret] = useState<string>('');
  const [factorId, setFactorId] = useState<string>('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    startEnrollment();
  }, []);

  const startEnrollment = async () => {
    try {
      setLoading(true);
      setError('');

      // First check if there are existing factors
      const { data: factors } = await supabase.auth.mfa.listFactors();
      
      // If there are existing factors, unenroll them first
      if (factors && factors.totp && factors.totp.length > 0) {
        for (const factor of factors.totp) {
          await supabase.auth.mfa.unenroll({ factorId: factor.id });
        }
      }

      // Now enroll a new factor
      const data = await enrollMFA();
      if (data && data.totp) {
        // Ensure we're using the URI format, not the entire object
        const qrCodeUri = data.totp.qr_code || data.totp.uri;
        const secretKey = data.totp.secret;
        
        if (!qrCodeUri || !secretKey) {
          setError('Fehler beim Abrufen der QR-Code-Daten.');
          return;
        }
        
        setQrCode(qrCodeUri);
        setSecret(secretKey);
        setFactorId(data.id);
      } else {
        setError('Fehler beim Einrichten der Zwei-Faktor-Authentifizierung. Bitte versuchen Sie es erneut.');
      }
    } catch (err: any) {
      console.error('MFA enrollment error:', err);
      setError('Ein Fehler ist aufgetreten. Bitte laden Sie die Seite neu und versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (code: string) => {
    const success = await verifyMFA(factorId, code);
    if (success) {
      generateAndSaveBackupCodes();
      setStep('backup');
    }
  };

  const generateAndSaveBackupCodes = async () => {
    const codes = generateBackupCodes(10);
    setBackupCodes(codes);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    for (const code of codes) {
      const hash = await hashBackupCode(code);
      await supabase.from('mfa_backup_codes').insert({
        user_id: user.id,
        code_hash: hash
      });
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    
    toast({
      title: 'Kopiert',
      description: 'Secret-Key wurde kopiert'
    });
  };

  const handleFinish = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>

          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Zwei-Faktor-Authentifizierung
            </h1>
            <p className="text-muted-foreground">
              Schützen Sie Ihr Konto mit einer zusätzlichen Sicherheitsebene
            </p>
          </div>

          {step === 'enroll' && (
            <Card className="p-8 backdrop-blur-md bg-background/60 border-primary/20">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12 space-y-4">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Einrichtung wird vorbereitet...</p>
                </div>
              ) : error ? (
                <div className="space-y-6">
                  <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <p className="text-sm text-destructive">{error}</p>
                  </div>
                  <Button
                    onClick={startEnrollment}
                    variant="outline"
                    className="w-full"
                  >
                    Erneut versuchen
                  </Button>
                  <Button
                    onClick={() => navigate('/dashboard')}
                    variant="ghost"
                    className="w-full"
                  >
                    Zurück zum Dashboard
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold mb-2">Schritt 1: QR-Code scannen</h2>
                    <p className="text-sm text-muted-foreground mb-6">
                      Scannen Sie diesen QR-Code mit Ihrer Authenticator-App (z.B. Google Authenticator, Microsoft Authenticator)
                    </p>
                    
                    <div className="flex justify-center p-6 bg-white rounded-lg">
                      {qrCode && <QRCodeSVG value={qrCode} size={256} />}
                    </div>
                  </div>

                <div className="border-t border-border/50 pt-6">
                  <p className="text-sm text-muted-foreground mb-3">
                    Oder geben Sie diesen Code manuell ein:
                  </p>
                  <div className="flex items-center gap-2 p-4 bg-muted/50 rounded-lg border border-border/50">
                    <code className="flex-1 text-sm font-mono break-all">{secret}</code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={copySecret}
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-primary" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                  <Button
                    onClick={() => setStep('verify')}
                    className="w-full"
                    size="lg"
                  >
                    Weiter zur Verifizierung
                  </Button>
                </div>
              )}
            </Card>
          )}

          {step === 'verify' && (
            <Card className="p-8 backdrop-blur-md bg-background/60 border-primary/20">
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold mb-2">Schritt 2: Code eingeben</h2>
                  <p className="text-sm text-muted-foreground mb-6">
                    Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein
                  </p>
                </div>

                <MFACodeInput onComplete={handleVerify} />

                <Button
                  variant="ghost"
                  onClick={() => setStep('enroll')}
                  className="w-full"
                >
                  Zurück zum QR-Code
                </Button>
              </div>
            </Card>
          )}

          {step === 'backup' && (
            <div className="space-y-6">
              <Card className="p-8 backdrop-blur-md bg-background/60 border-primary/20">
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold">Schritt 3: Backup-Codes speichern</h2>
                  <p className="text-sm text-muted-foreground">
                    Bewahren Sie diese Codes sicher auf. Sie können jeden Code nur einmal verwenden, falls Sie keinen Zugriff auf Ihre Authenticator-App haben.
                  </p>
                </div>
              </Card>

              <BackupCodesDisplay codes={backupCodes} />

              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <p className="text-sm text-foreground">
                  ✓ Zwei-Faktor-Authentifizierung wurde erfolgreich aktiviert!
                </p>
              </div>

              <Button
                onClick={handleFinish}
                className="w-full"
                size="lg"
              >
                Zum Dashboard
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
